import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { getFileIcon } from '../../utils/fileUtils';
import { extensionOf, getFriendlyFileType } from '../../utils/previewUtils';
import { formatFileSize } from '../../utils/formatters';

export interface PreviewEmptyStatePrimaryAction {
  label: string;
  onClick: () => void;
  startIcon?: React.ReactNode;
}

export interface PreviewEmptyStateProps {
  fileName: string;
  mimeType?: string;
  size?: number;
  /** When set, shown as the main heading (defaults to fileName for office/first-party). */
  title?: string;
  subtitle?: string;
  /** Friendly type label; derived from name/mime if omitted. */
  typeLabel?: string;
  variant?: 'error' | 'office' | 'unsupported';
  primaryAction?: PreviewEmptyStatePrimaryAction;
  onDownload?: () => void;
  onRetry?: () => void;
  onOpenNewTab?: () => void;
}

function officeIcon(ext: string) {
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') {
    return <TableChartIcon sx={{ fontSize: 72, color: 'success.main' }} />;
  }
  if (ext === 'ppt' || ext === 'pptx') {
    return <SlideshowIcon sx={{ fontSize: 72, color: 'warning.main' }} />;
  }
  if (ext === 'doc' || ext === 'docx') {
    return <DescriptionIcon sx={{ fontSize: 72, color: 'info.main' }} />;
  }
  return <InsertDriveFileIcon sx={{ fontSize: 72, color: 'text.secondary' }} />;
}

export const PreviewEmptyState: React.FC<PreviewEmptyStateProps> = ({
  fileName,
  mimeType = 'application/octet-stream',
  size = 0,
  title,
  subtitle,
  typeLabel,
  variant = 'unsupported',
  primaryAction,
  onDownload,
  onRetry,
  onOpenNewTab,
}) => {
  const ext = extensionOf(fileName);
  const friendlyType = typeLabel || getFriendlyFileType(fileName, mimeType);
  const heading =
    title ??
    (variant === 'office' ? fileName : 'Preview unavailable');

  const icon =
    variant === 'office' ? (
      officeIcon(ext)
    ) : (
      <Box sx={{ fontSize: 72, display: 'flex', justifyContent: 'center' }}>
        {getFileIcon({ name: fileName, mimeType }, 72)}
      </Box>
    );

  const metaParts: string[] = [];
  if (friendlyType) metaParts.push(friendlyType);
  if (size > 0) metaParts.push(formatFileSize(size));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        textAlign: 'center',
        maxWidth: 440,
        mx: 'auto',
      }}
    >
      {icon}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, wordBreak: 'break-word' }}>
        {heading}
      </Typography>
      {metaParts.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: subtitle ? 1 : 2 }}>
          {metaParts.join(' · ')}
        </Typography>
      )}
      {subtitle && (
        <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      <Stack direction="column" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
        {primaryAction && (
          <Button
            variant="contained"
            size="large"
            startIcon={primaryAction.startIcon}
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
          {onRetry && (
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRetry}>
              Retry
            </Button>
          )}
          {onOpenNewTab && (
            <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={onOpenNewTab}>
              Open in new tab
            </Button>
          )}
          {onDownload && (
            <Button
              variant={primaryAction ? 'text' : 'contained'}
              startIcon={<DownloadIcon />}
              onClick={onDownload}
            >
              Download
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PreviewEmptyState;
